import { client as axios } from '../../axios/client';
import {
  GET_BOARDS,
  GET_BOARDS_SUCCESS,
  GET_BOARDS_ERROR,
  CREATE_BOARD,
  CREATE_BOARD_SUCCESS,
  CREATE_BOARD_ERROR,
  UPDATE_BOARD,
  UPDATE_BOARD_SUCCESS,
  UPDATE_BOARD_ERROR,
  DELETE_BOARD,
  DELETE_BOARD_SUCCESS,
  DELETE_BOARD_ERROR,
  TOGGLE_PINNED_BOARD,
  GET_PINNED_BOARDS,
  GET_PINNED_BOARDS_SUCCESS,
  GET_PINNED_BOARDS_ERROR,
  SET_SELECTED_BOARD_ID,
  CLEAR_SELECTED_BOARD_ID,
} from './kanbanTypes';
import { createColumn } from './columnActions';

// Utilidad para capitalizar la primera letra de un texto
// Principio SRP: Esta función tiene una única responsabilidad: capitalizar textos.
const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Utilidad para capitalizar claves de un objeto meta_data
// Principio SRP: Capitaliza los valores string de un objeto meta_data.
const capitalizeMetaData = (metaData) => {
  const capitalized = {};
  for (const key in metaData) {
    if (Object.prototype.hasOwnProperty.call(metaData, key)) {
      const value = metaData[key];
      capitalized[key] = typeof value === 'string' ? capitalizeFirstLetter(value) : value;
    }
  }
  return capitalized;
};

/**
 * Obtiene todos los tableros según filtros
 * Principio SRP: Esta función solo obtiene tableros, no modifica ni crea otros recursos.
 * Principio OCP: Permite agregar nuevos filtros sin modificar la lógica principal.
 */
export const getBoards = (filters = {}) => async (dispatch) => {
  dispatch({ type: GET_BOARDS });
  try {
    if (!filters.id_customer) {
      dispatch({ type: GET_BOARDS_SUCCESS, payload: [] });
      return { items: [] };
    }
    const params = {
      skip: filters.skip || 0,
      limit: filters.limit || 10,
      ...(filters.id_customer && { id_customer: filters.id_customer }),
      ...(filters.id_agent && { id_agent: filters.id_agent }),
      ...(filters.board_type && { board_type: filters.board_type }),
      ...(filters.pinned !== undefined && { pinned: filters.pinned })
    };
    const headers = filters.token ? { Authorization: `Bearer ${filters.token}` } : {};
    const response = await axios.get('/kanban/boards', { params, headers });
    dispatch({ type: GET_BOARDS_SUCCESS, payload: response.data.items || [] });
    return response.data;
  } catch (error) {
    dispatch({ type: GET_BOARDS_ERROR, payload: error.message });
    return { items: [] };
  }
};

/**
 * Crea las columnas iniciales para un tablero según su tipo
 * Principio SRP: Solo crea columnas iniciales, no modifica el tablero ni otros recursos.
 * Principio LSP: Puede extenderse para otros tipos de tablero sin romper la funcionalidad.
 */
export const createInitialColumns = (board, token) => async (dispatch, getState) => {
  try {
    if (!board || !board.id) throw new Error('Se requiere el ID del tablero');
    if (!token) throw new Error('Se requiere el token de autenticación');

    // Verificar si ya existen columnas para este tablero
    const state = getState();
    // Adaptación a la nueva estructura del estado después de la refactorización
    const existingColumns = state.kanban.columns?.items?.filter(col =>
      col.board_id === board.id || col.board_id === Number(board.id)
    ) || [];

    // Si ya hay columnas, no crear nuevas
    if (existingColumns.length > 0) {
      return existingColumns;
    }

    const boardType = board.board_type || 'general';
    const initialColumns = boardType === 'soporte' ? [
      { name: 'Ticket Creado', position: 1, meta_data: JSON.stringify({ type: 'soporte' }) },
      { name: 'En Atención', position: 2, meta_data: JSON.stringify({ type: 'soporte' }) },
      { name: 'Pendiente', position: 3, meta_data: JSON.stringify({ type: 'soporte' }) },
      { name: 'Resuelto', position: 4, meta_data: JSON.stringify({ type: 'soporte' }) }
    ] : [
      { name: 'Ticket Creado', position: 1, meta_data: JSON.stringify({ type: 'general' }) }
    ];

    const createdColumns = [];
    for (const column of initialColumns) {
      try {
        const result = await dispatch(createColumn(board.id, {
          name: column.name,
          position: column.position,
          meta_data: column.meta_data,
          id_customer: board.id_customer,
          isInitialCreation: true, // Flag para identificar creación inicial
          token
        }));
        createdColumns.push(result);
      } catch (error) {
        console.error(`Error al crear columna ${column.name}:`, error);
      }
    }
    return createdColumns;
  } catch (error) {
    console.error('Error al crear columnas iniciales:', error);
    throw error;
  }
};

/**
 * Crea un nuevo tablero
 * Principio SRP: Solo crea el tablero y sus columnas iniciales si corresponde.
 * Principio OCP: Permite agregar nuevos tipos de tablero fácilmente.
 */
export const createBoard = (boardData) => async (dispatch) => {
  try {
    dispatch({ type: CREATE_BOARD });
    const { token, ...restData } = boardData;
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    if (!restData.name) throw new Error('El nombre del tablero es obligatorio');
    if (!restData.id_customer) throw new Error('El ID del cliente es obligatorio');
    if (!restData.id_agent) throw new Error('El ID del agente es obligatorio');
    const formattedData = {
      name: capitalizeFirstLetter(restData.name.trim()),
      id_customer: restData.id_customer,
      id_agent: restData.id_agent
    };
    if (restData.description) formattedData.description = capitalizeFirstLetter(restData.description.trim());
    formattedData.created_by = restData.created_by ? String(restData.created_by) : "1";
    
    // Mapeo simple de board types - solo los valores reales que se guardan en la BD
    const boardTypeMap = {
      'general': 'general',
      'soporte': 'soporte',
      'support': 'soporte',    // Solo mapear 'support' del frontend a 'soporte' de la BD
      'flujo_bot': 'flujo_bot'
    };
    
    formattedData.board_type = restData.board_type ? (boardTypeMap[restData.board_type] || 'general') : 'general';
    if (restData.pinned !== undefined) formattedData.pinned = Boolean(restData.pinned);
    if (restData.meta_data) {
      if (typeof restData.meta_data === 'object') {
        formattedData.meta_data = JSON.stringify(capitalizeMetaData(restData.meta_data));
      } else if (typeof restData.meta_data === 'string') {
        try {
          formattedData.meta_data = JSON.stringify(capitalizeMetaData(JSON.parse(restData.meta_data)));
        } catch (e) {
          formattedData.meta_data = '{}';
        }
      } else {
        formattedData.meta_data = '{}';
      }
    } else {
      formattedData.meta_data = '{}';
    }
    const apiParams = {
      name: formattedData.name,
      id_customer: String(formattedData.id_customer),
      id_agent: String(formattedData.id_agent),
      created_by: String(formattedData.created_by),
      board_type: formattedData.board_type,
      meta_data: formattedData.meta_data
    };
    if (formattedData.pinned !== undefined) apiParams.pinned = formattedData.pinned;
    const response = await axios.post('/kanban/boards', null, {
      params: apiParams,
      headers: { Authorization: authToken }
    });
    // Primero crear las columnas iniciales si corresponde
    let initialColumns = [];
    if (formattedData.board_type === 'general' || formattedData.board_type === 'soporte') {
      try {
        initialColumns = await dispatch(createInitialColumns(response.data, token));
      } catch (columnError) {
        console.error('Error al crear columnas iniciales:', columnError);
      }
    }

    // Luego actualizar el estado con el tablero creado
    dispatch({ type: CREATE_BOARD_SUCCESS, payload: response.data });

    // Devolver el tablero con sus columnas
    return {
      ...response.data,
      columns: initialColumns
    };
  } catch (error) {
    if (error.response && error.response.status === 422) {
      const validationErrors = error.response.data.detail || [];
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        validationErrors.forEach(() => {});
      }
    }
    dispatch({ type: CREATE_BOARD_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Actualiza un tablero existente
 * Principio SRP: Solo actualiza los datos del tablero especificado.
 * Principio OCP: Permite agregar nuevos campos a actualizar sin modificar la lógica principal.
 */
export const updateBoard = (boardId, boardData) => async (dispatch) => {
  dispatch({ type: UPDATE_BOARD });
  try {
    const { token, ...data } = boardData;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = {};
    if (data.name !== undefined) params.name = capitalizeFirstLetter(data.name.trim());
    if (data.description !== undefined) params.description = capitalizeFirstLetter(data.description.trim());
    if (data.pinned !== undefined) params.pinned = data.pinned;
    if (data.id_agent !== undefined) params.id_agent = data.id_agent;
    if (data.meta_data !== undefined) {
      if (typeof data.meta_data === 'object') {
        params.meta_data = JSON.stringify(capitalizeMetaData(data.meta_data));
      } else if (typeof data.meta_data === 'string') {
        try {
          params.meta_data = JSON.stringify(capitalizeMetaData(JSON.parse(data.meta_data)));
        } catch (e) {
          params.meta_data = data.meta_data;
        }
      } else {
        params.meta_data = data.meta_data;
      }
    }
    const response = await axios.put(`/kanban/boards/${boardId}`, null, { params, headers });
    dispatch({ type: UPDATE_BOARD_SUCCESS, payload: response.data });
    return response.data;
  } catch (error) {
    dispatch({ type: UPDATE_BOARD_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Elimina un tablero
 * Principio SRP: Solo elimina el tablero especificado.
 */
export const deleteBoard = (boardId) => async (dispatch) => {
  dispatch({ type: DELETE_BOARD });
  try {
    await axios.delete(`/kanban/boards/${boardId}`);
    dispatch({ type: DELETE_BOARD_SUCCESS, payload: boardId });
  } catch (error) {
    dispatch({ type: DELETE_BOARD_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Alterna el estado de fijado de un tablero
 * Principio SRP: Solo alterna el estado de fijado del tablero.
 */
export const togglePinnedBoard = (boardId, isPinned, token) => async (dispatch) => {
  try {
    // Validar parámetros
    if (!boardId) {
      throw new Error('ID de tablero no válido');
    }

    // Asegurarse de que isPinned sea un booleano
    const currentPinned = Boolean(isPinned);

    // Preparar encabezados con token de autenticación
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Preparar parámetros para la API
    const params = { pinned: !currentPinned };

    // Actualizar el estado en Redux inmediatamente para una respuesta UI más rápida
    dispatch({
      type: TOGGLE_PINNED_BOARD,
      payload: {
        boardId: String(boardId),
        pinned: !currentPinned
      }
    });

    // Realizar la petición a la API
    const response = await axios.put(
      `/kanban/boards/${boardId}`,
      null,
      { params, headers }
    );

    // Devolver los datos de la respuesta
    return response.data;
  } catch (error) {
    console.error('Error al alternar estado de fijado:', error);
    // Revertir el cambio en Redux si la API falla
    dispatch({
      type: TOGGLE_PINNED_BOARD,
      payload: {
        boardId: String(boardId),
        pinned: isPinned
      }
    });
    throw error;
  }
};

/**
 * Obtiene tableros fijados
 * Principio SRP: Solo obtiene tableros que están fijados.
 */
export const getPinnedBoards = (filters = {}) => async (dispatch) => {
  dispatch({ type: GET_PINNED_BOARDS });
  try {
    if (!filters.id_customer) {
      dispatch({ type: GET_PINNED_BOARDS_SUCCESS, payload: [] });
      return { items: [] };
    }
    const params = {
      pinned: true,
      skip: filters.skip || 0,
      limit: filters.limit || 10,
      ...(filters.id_customer && { id_customer: filters.id_customer }),
      ...(filters.id_agent && { id_agent: filters.id_agent })
    };
    const headers = filters.token ? { Authorization: `Bearer ${filters.token}` } : {};
    const response = await axios.get('/kanban/boards', { params, headers });
    dispatch({ type: GET_PINNED_BOARDS_SUCCESS, payload: response.data.items || [] });
    return response.data;
  } catch (error) {
    dispatch({ type: GET_PINNED_BOARDS_ERROR, payload: error.message });
    return { items: [] };
  }
};

/**
 * Acciones para navegación limpia sin parámetros en URL
 * Principio SRP: Solo manejan el estado de selección de tablero.
 */

/**
 * Selecciona un tablero para navegación limpia
 * @param {string|number} boardId - ID del tablero a seleccionar
 */
export const setSelectedBoardId = (boardId) => ({
  type: SET_SELECTED_BOARD_ID,
  payload: boardId
});

/**
 * Limpia la selección de tablero
 */
export const clearSelectedBoardId = () => ({
  type: CLEAR_SELECTED_BOARD_ID
});
